package ai.wagemix.logic.mixbuilder

import ai.wagemix.net.models.firebase.WMSubscriptionType
import co.touchlab.kermit.Severity
import com.fsryan.foundation.util.log.FSLog
import com.fsryan.foundation.util.log.sendEventInclusive

/*
 * The analytics events of the mix builder presenter (planning/analytics-plan.md §5, "Mix
 * building"). Re-evaluation events are shared with the job descriptions presenter and live in
 * FSLogWMStructuredEvents.kt.
 */

/** How the builder came to show a draft (`mix_started.origin`). */
internal enum class WMMixOrigin { Default, NewMix, SavedMix, JobDescription }

/** Which filter changed (`filter_changed.filter`, `filter_empty_result.filter`). */
internal enum class WMMixFilterKind { Area, Industry }

/** The kind of area a filter selects (`filter_changed.area_kind`), from the area's BLS type. */
internal enum class WMAreaKind { National, State, Territory, Metro, Nonmetro }

/** Whether a save created a stored mix or rewrote the one the draft came from (`mix_saved.kind`). */
internal enum class WMMixSaveKind { New, Update }

/**
 * How a tutorial step ended (`mix_tutorial_step_completed.by`): its Next or Done button, or the visitor did what the
 * step points at (added occupations, switched annual/hourly, changed the projection).
 */
internal enum class WMTutorialCompletion { Next, Action }

private inline fun FSLog.sendWMMixBuilderEvent(
    eventName: String,
    crossinline fields: MutableMap<String, Any>.() -> Unit = {}
) {
    sendEventInclusive(severity = Severity.Info, eventName = eventName) {
        fields()
    }
}

private fun WMMixBuilderTutorialStep.wire(): String = when (this) {
    WMMixBuilderTutorialStep.AddOccupations -> "add_occupations"
    WMMixBuilderTutorialStep.ChartData -> "chart_data"
    WMMixBuilderTutorialStep.ChartMode -> "chart_mode"
    WMMixBuilderTutorialStep.ChartProjection -> "chart_projection"
}

private fun WMMixFilterKind.wire(): String = when (this) {
    WMMixFilterKind.Area -> "area"
    WMMixFilterKind.Industry -> "industry"
}

internal fun FSLog.sendWMMixStarted(origin: WMMixOrigin, occupationCount: Int) {
    sendWMMixBuilderEvent(eventName = "WMMixStarted") {
        put(
            "origin",
            when (origin) {
                WMMixOrigin.Default -> "default"
                WMMixOrigin.NewMix -> "new_mix"
                WMMixOrigin.SavedMix -> "saved_mix"
                WMMixOrigin.JobDescription -> "job_description"
            }
        )
        put("occupation_count", occupationCount)
    }
}

internal fun FSLog.sendWMOccupationRemoved(occupationCount: Int) {
    sendWMMixBuilderEvent(eventName = "WMOccupationRemoved") {
        put("occupation_count", occupationCount)
    }
}

internal fun FSLog.sendWMOccupationWeightChanged(occupationCount: Int, lockedCount: Int) {
    sendWMMixBuilderEvent(eventName = "WMOccupationWeightChanged") {
        put("occupation_count", occupationCount)
        put("locked_count", lockedCount)
    }
}

internal fun FSLog.sendWMOccupationToggled(enabled: Boolean, occupationCount: Int) {
    sendWMMixBuilderEvent(eventName = "WMOccupationToggled") {
        put("enabled", enabled)
        put("occupation_count", occupationCount)
    }
}

internal fun FSLog.sendWMOccupationLocked(locked: Boolean) {
    sendWMMixBuilderEvent(eventName = "WMOccupationLocked") {
        put("locked", locked)
    }
}

internal fun FSLog.sendWMChartModeChanged(hourly: Boolean) {
    sendWMMixBuilderEvent(eventName = "WMChartModeChanged") {
        put("hourly", hourly)
    }
}

internal fun FSLog.sendWMProjectionChanged(projectedYears: Int) {
    sendWMMixBuilderEvent(eventName = "WMProjectionChanged") {
        put("projected_years", projectedYears)
    }
}

/** [areaKind] is set only for an area that was selected (not cleared) and is known to the area list. */
internal fun FSLog.sendWMFilterChanged(filter: WMMixFilterKind, cleared: Boolean, areaKind: WMAreaKind?) {
    sendWMMixBuilderEvent(eventName = "WMFilterChanged") {
        put("filter", filter.wire())
        put("cleared", cleared)
        areaKind?.let { kind ->
            put(
                "area_kind",
                when (kind) {
                    WMAreaKind.National -> "national"
                    WMAreaKind.State -> "state"
                    WMAreaKind.Territory -> "territory"
                    WMAreaKind.Metro -> "metro"
                    WMAreaKind.Nonmetro -> "nonmetro"
                }
            )
        }
    }
}

internal fun FSLog.sendWMFilterEmptyResult(filter: WMMixFilterKind) {
    sendWMMixBuilderEvent(eventName = "WMFilterEmptyResult") {
        put("filter", filter.wire())
    }
}

internal fun FSLog.sendWMPremiumGateHit(feature: WMPremiumFeature, plan: WMSubscriptionType) {
    sendWMMixBuilderEvent(eventName = "WMPremiumGateHit") {
        put("feature", feature.name)
        put("plan", plan.stringValue)
    }
}

internal fun FSLog.sendWMMixSaved(
    kind: WMMixSaveKind,
    mixId: String,
    occupationCount: Int,
    hasFilters: Boolean,
    fromJobDescription: Boolean,
) {
    sendWMMixBuilderEvent(eventName = "WMMixSaved") {
        put(
            "kind",
            when (kind) {
                WMMixSaveKind.New -> "new"
                WMMixSaveKind.Update -> "update"
            }
        )
        put("mix_id", mixId)
        put("occupation_count", occupationCount)
        put("has_filters", hasFilters)
        put("from_job_description", fromJobDescription)
    }
}

/** [mixId] is the stored mix the discarded draft was opened from; a never-saved draft has none. */
internal fun FSLog.sendWMMixDiscarded(mixId: String?) {
    sendWMMixBuilderEvent(eventName = "WMMixDiscarded") {
        mixId?.let { put("mix_id", it) }
    }
}

internal fun FSLog.sendWMMixTutorialStepCompleted(step: WMMixBuilderTutorialStep, by: WMTutorialCompletion) {
    sendWMMixBuilderEvent(eventName = "WMMixTutorialStepCompleted") {
        put("step", step.wire())
        put(
            "by",
            when (by) {
                WMTutorialCompletion.Next -> "next"
                WMTutorialCompletion.Action -> "action"
            }
        )
    }
}

internal fun FSLog.sendWMMixTutorialSkipped(step: WMMixBuilderTutorialStep) {
    sendWMMixBuilderEvent(eventName = "WMMixTutorialSkipped") {
        put("step", step.wire())
    }
}
